import { formatBytes, isImageType, type Attachment } from '@agent-platform/shared';
import { CircleAlert, FileText, LoaderCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fileContentUrl } from '@/lib/files';
import { cn } from '@/lib/utils';

/** A file being attached in the composer. */
export interface PendingAttachment {
  localId: string;
  name: string;
  sizeBytes: number;
  /** Local preview (images only), revoked when removed. */
  previewUrl: string | null;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  attachment?: Attachment;
  error?: string;
}

function Thumb({ src, name }: { src: string; name: string }) {
  return (
    <img
      src={src}
      alt={name}
      className="size-full object-cover"
      loading="lazy"
      decoding="async"
      draggable={false}
    />
  );
}

/** Chips for the composer: thumbnail or icon, name, size, progress, remove. */
export function PendingAttachments({
  items,
  onRemove,
}: {
  items: PendingAttachment[];
  onRemove: (localId: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2 px-3 pt-3" aria-label="Attachments">
      {items.map((a) => (
        <li
          key={a.localId}
          data-testid="pending-attachment"
          data-status={a.status}
          className={cn(
            'bg-muted/60 relative flex max-w-56 items-center gap-2 rounded-xl border p-1.5 pr-8 text-xs',
            a.status === 'error' && 'border-destructive/50',
          )}
          title={a.error ?? a.name}
        >
          <span className="bg-background relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border">
            {a.previewUrl ? (
              <Thumb src={a.previewUrl} name={a.name} />
            ) : (
              <FileText className="text-muted-foreground size-4" aria-hidden />
            )}
            {a.status === 'uploading' && (
              <span className="bg-background/70 absolute inset-0 flex items-center justify-center">
                <LoaderCircle className="size-4 animate-spin" aria-label="Uploading" />
              </span>
            )}
          </span>
          <span className="grid min-w-0">
            <span className="truncate font-medium">{a.name}</span>
            <span
              className={cn(
                'text-muted-foreground truncate',
                a.status === 'error' && 'text-destructive',
              )}
            >
              {a.status === 'error' ? (
                <span className="inline-flex items-center gap-1">
                  <CircleAlert className="size-3" aria-hidden /> {a.error}
                </span>
              ) : a.status === 'uploading' ? (
                `Uploading ${Math.round(a.progress * 100)}%`
              ) : (
                formatBytes(a.sizeBytes)
              )}
            </span>
          </span>
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            className="absolute top-1 right-1"
            aria-label={`Remove ${a.name}`}
            onClick={() => onRemove(a.localId)}
          >
            <X aria-hidden />
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** Attachments of a sent message: image thumbnails and file chips (open in a new tab). */
export function MessageAttachments({
  attachments,
  className,
}: {
  attachments: Attachment[];
  className?: string;
}) {
  if (attachments.length === 0) return null;
  return (
    <ul className={cn('flex flex-wrap justify-end gap-2', className)} aria-label="Attached files">
      {attachments.map((a) => (
        <li key={a.fileId}>
          <a
            href={fileContentUrl(a.fileId)}
            target="_blank"
            rel="noreferrer"
            className="block focus-visible:outline-2"
            data-testid="message-attachment"
          >
            {isImageType(a.mediaType) ? (
              <span className="bg-muted block size-28 overflow-hidden rounded-xl border">
                <Thumb src={fileContentUrl(a.fileId)} name={a.name} />
              </span>
            ) : (
              <span className="bg-muted/60 flex max-w-56 items-center gap-2 rounded-xl border p-2 text-xs">
                <FileText className="text-muted-foreground size-4 shrink-0" aria-hidden />
                <span className="grid min-w-0">
                  <span className="truncate font-medium">{a.name}</span>
                  <span className="text-muted-foreground">{formatBytes(a.sizeBytes)}</span>
                </span>
              </span>
            )}
          </a>
        </li>
      ))}
    </ul>
  );
}
